import { useEffect, useRef, useState } from "react";
import { LuCircleHelp, LuFlame, LuGem, LuKeyRound, LuLock, LuScrollText } from "react-icons/lu";
import { AvatarBare } from "../components/Avatar";
import { FeedbackBox, QuestionCard } from "../components/QuestionCard";
import { banner, pop, useOnChange } from "../components/juice";
import { buzz, play } from "../sound";

import type { GameProps } from "../pages/Match";

/* ───────────── Skattejagt ───────────── */
function Item({ ch }: { ch: string }) {
  switch (ch) {
    case "K": return <span className="it key"><LuKeyRound size={"62%"} aria-hidden="true" /></span>;
    case "C": return <span className="it clue"><LuScrollText size={"60%"} aria-hidden="true" /></span>;
    case "X": return <span className="it lock"><LuLock size={"58%"} aria-hidden="true" /></span>;
    case "G": return <span className="it gate"><LuCircleHelp size={"62%"} aria-hidden="true" /></span>;
    case "T": return <span className="it gem"><LuGem size={"70%"} aria-hidden="true" /></span>;
    default: return null;
  }
}

export function Skattejagt(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const hasQuestion = !!st.question;
  const opened = new Set<string>(b.opened);
  const taken = new Set<string>(b.taken);
  const canMove = b.phase === "play" && !hasQuestion && !p.busy;
  const rows: number = b.rows ?? b.cells.length, cols: number = b.cols;
  const [bump, setBump] = useState<{ dx: number; dy: number } | null>(null);
  const bumpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bonk = (dx: number, dy: number) => {
    play("bump"); buzz(25);
    setBump({ dx, dy });
    if (bumpTimer.current) clearTimeout(bumpTimer.current);
    bumpTimer.current = setTimeout(() => setBump(null), 260);
  };
  useEffect(() => () => { if (bumpTimer.current) clearTimeout(bumpTimer.current); }, []);
  const move = (dx: number, dy: number) => {
    if (!canMove) return;
    const nx = b.x + dx, ny = b.y + dy;
    const ch: string | undefined = b.cells[ny]?.[nx];
    /* a wall we can already see needs no round trip (and no error message) – just a friendly little bump */
    if (ch === undefined || ch === "#") { bonk(dx, dy); return; }
    void p.act({ type: "move", dx, dy });
  };
  const me = p.view.players.find((x: any) => x.seat === p.view.you);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.matches?.("input,textarea,select")) return;
      const m: Record<string, [number, number]> = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0], w: [0, -1], s: [0, 1], a: [-1, 0], d: [1, 0] };
      const d = m[e.key];
      if (d) { e.preventDefault(); move(d[0], d[1]); }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  });

  /* swipe on a touch screen */
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const onDown = (e: any) => { if (e.pointerType !== "mouse") swipe.current = { x: e.clientX, y: e.clientY }; };
  const onUp = (e: any) => {
    const s0 = swipe.current; swipe.current = null;
    if (!s0) return;
    const dx = e.clientX - s0.x, dy = e.clientY - s0.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 24) return;
    if (Math.abs(dx) > Math.abs(dy)) move(dx > 0 ? 1 : -1, 0); else move(0, dy > 0 ? 1 : -1);
  };

  /* sounds and little announcements */
  useOnChange(`${b.x},${b.y}`, () => play("step"), p.view.id);
  useOnChange(b.keys as number, (n: number, o: number) => { if (n > o) { play("key"); pop("+1 nøgle", "gold"); buzz(30); } }, p.view.id);
  useOnChange(b.clues as number, (n: number, o: number) => { if (n > o) { play("flip"); pop("+1 spor", "blue"); } }, p.view.id);
  useOnChange(b.opened.length as number, (n: number, o: number) => { if (n > o) { play("gate"); pop("Porten er åben!", "good"); buzz([30, 30, 60]); } }, p.view.id);
  useOnChange(b.torches as number, (n: number, o: number) => { if (n < o && n <= 2 && n > 0) banner(n === 1 ? "Kun 1 fakkel tilbage!" : "Kun 2 fakler tilbage!", "soft"); }, p.view.id);
  useOnChange(!!b.won, (w: boolean) => { if (w) { play("treasure"); banner("Skatten er fundet!", "gold"); buzz([40, 40, 40, 40, 120]); } }, p.view.id);

  return (
    <div className="arena-grid wide">
      <div className="arena-center">
      <div className="parch">
      <div className="panel-title">Ruinen</div>
      <div className="treasure" onPointerDown={onDown} onPointerUp={onUp} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, ["--cols" as any]: cols, ["--rows" as any]: rows }} role="img" aria-label={`Ruinen. Du står på felt ${b.x + 1}, ${b.y + 1}. Du har ${b.keys} af ${b.keysNeeded} nøgler.`}>
        {b.cells.flatMap((row: string, y: number) =>
          row.split("").map((ch: string, x: number) => {
            const k = `${x},${y}`;
            let cls = "floor", item: string | null = null;
            if (ch === "?") cls = "fog";
            else if (ch === "#") cls = "wall";
            else if (ch in { K: 1, C: 1, X: 1, G: 1, T: 1 }) {
              const gone = (ch === "K" || ch === "C") && taken.has(k);
              const open = (ch === "G" || ch === "X") && opened.has(k);
              if (open) cls = "floor opened"; else if (!gone) item = ch;
            }
            return <div key={k} className={`${cls} ${(x + y) % 2 ? "alt" : ""} ${item ? "item" : ""}`}>{item && <Item ch={item} />}</div>;
          }),
        )}
        <div className={`hero ${bump ? "bump" : ""}`} style={{ ["--bx" as any]: `${(bump?.dx ?? 0) * 7}px`, ["--by" as any]: `${(bump?.dy ?? 0) * 7}px`, left: `${(b.x / cols) * 100}%`, top: `${(b.y / rows) * 100}%`, width: `${100 / cols}%`, height: `${100 / rows}%` }} aria-hidden="true">
          <span className="lantern" />
          <AvatarBare equipped={me?.avatar} size={100} className="herosvg" />
        </div>
      </div>
      </div>
      {b.phase === "play" && (
        <div className="dpad" aria-label="Styring (du kan også bruge piletaster, WASD eller stryge på banen)">
          <span /><button className="btn ghost" disabled={!canMove} aria-label="Op" onClick={() => move(0, -1)}>▲</button><span />
          <button className="btn ghost" disabled={!canMove} aria-label="Venstre" onClick={() => move(-1, 0)}>◀</button>
          <button className="btn ghost" disabled={!canMove} aria-label="Ned" onClick={() => move(0, 1)}>▼</button>
          <button className="btn ghost" disabled={!canMove} aria-label="Højre" onClick={() => move(1, 0)}>▶</button>
        </div>
      )}
      <div className="motto">Find skatten</div>
      </div>
      <div className="sidecol r">
      {hasQuestion && <div className="notice" role="status">En port blokerer vejen! Svar rigtigt for at åbne den.</div>}
      {hasQuestion && <QuestionCard q={st.question} serverNow={st.now} onAnswer={p.answer} busy={p.busy} totalMs={45000} />}
      {!hasQuestion && st.feedback && <FeedbackBox fb={st.feedback} />}
      <section className="card" aria-live="polite">
        <div className="panel-title">Din beholdning</div>
        <div className="statline"><span className="inv"><LuKeyRound size={16} aria-hidden="true" /> Nøgler</span><b>{b.keys}/{b.keysNeeded}</b></div>
        <div className="statline"><span className="inv"><LuScrollText size={16} aria-hidden="true" /> Spor</span><b>{b.clues}</b></div>
        <div className="statline"><span className="inv"><LuFlame size={16} aria-hidden="true" /> Fakler</span><b className="torches" aria-label={`${b.torches} fakler`}>{Array.from({ length: Math.min(8, Math.max(0, b.torches)) }, (_, i) => <LuFlame key={i} size={15} aria-hidden="true" />)}<span className="sr-only">{b.torches}</span></b></div>
        <div className="statline"><span>Skridt</span><b>{b.moves}</b></div>
      </section>
      <small className="muted">Find {b.keysNeeded} nøgler, åbn den sidste port (<LuLock size={13} aria-hidden="true" />) og find skatten (<LuGem size={13} aria-hidden="true" />). Fakler lyser op i mørket – hvert skridt i ukendt terræn koster.</small>
      {b.phase === "over" && <div className={`notice ${b.won ? "good" : "error"}`} role="status">{b.won ? "Du fandt skatten!" : "Fakkelskær og skridt slap op. Prøv igen!"}</div>}
      </div>
    </div>
  );
}

